/**
 * @quotech/api - React Query Hooks
 * 
 * Pre-configured React Query hooks for API operations.
 */

import { 
  useQuery, 
  useMutation, 
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
  type QueryKey,
} from '@tanstack/react-query'
import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import { getDefaultClient } from './client'
import type { ApiError, PaginatedResponse, RequestOptions } from './types'

/**
 * Hook to get the API client
 */
export function useApiClient(): AxiosInstance {
  return getDefaultClient()
}

/**
 * Generic GET hook
 */
export function useApiQuery<TData = unknown>(
  key: QueryKey,
  url: string,
  options?: Omit<UseQueryOptions<TData, ApiError>, 'queryKey' | 'queryFn'> & RequestOptions
) {
  const client = useApiClient()

  return useQuery<TData, ApiError>({
    queryKey: key,
    queryFn: async () => {
      const response = await client.get<TData>(url, options as AxiosRequestConfig)
      return response.data
    },
    ...options,
  })
}

/**
 * Paginated GET hook
 */
export function usePaginatedQuery<TData = unknown>(
  key: QueryKey,
  url: string,
  options?: Omit<UseQueryOptions<PaginatedResponse<TData>, ApiError>, 'queryKey' | 'queryFn'> & RequestOptions
) {
  const client = useApiClient()

  return useQuery<PaginatedResponse<TData>, ApiError>({
    queryKey: key,
    queryFn: async () => {
      const response = await client.get<PaginatedResponse<TData>>(url, options as AxiosRequestConfig)
      return response.data
    },
    ...options,
  })
}

/**
 * Generic POST mutation hook
 */
export function useApiMutation<TData = unknown, TVariables = unknown>(
  url: string,
  options?: Omit<UseMutationOptions<TData, ApiError, TVariables>, 'mutationFn'> & RequestOptions
) {
  const client = useApiClient()

  return useMutation<TData, ApiError, TVariables>({
    mutationFn: async (variables: TVariables) => {
      const response = await client.post<TData>(url, variables, options as AxiosRequestConfig)
      return response.data
    },
    ...options,
  })
}

/**
 * Generic PUT mutation hook
 */
export function useApiPut<TData = unknown, TVariables = unknown>(
  url: string,
  options?: Omit<UseMutationOptions<TData, ApiError, TVariables>, 'mutationFn'> & RequestOptions
) {
  const client = useApiClient()

  return useMutation<TData, ApiError, TVariables>({
    mutationFn: async (variables: TVariables) => {
      const response = await client.put<TData>(url, variables, options as AxiosRequestConfig)
      return response.data
    },
    ...options,
  })
}

/**
 * Generic PATCH mutation hook
 */
export function useApiPatch<TData = unknown, TVariables = unknown>(
  url: string,
  options?: Omit<UseMutationOptions<TData, ApiError, TVariables>, 'mutationFn'> & RequestOptions
) {
  const client = useApiClient()

  return useMutation<TData, ApiError, TVariables>({
    mutationFn: async (variables: TVariables) => {
      const response = await client.patch<TData>(url, variables, options as AxiosRequestConfig)
      return response.data
    },
    ...options,
  })
}

/**
 * Generic DELETE mutation hook
 */
export function useApiDelete<TData = unknown>(
  url: string,
  options?: Omit<UseMutationOptions<TData, ApiError, void>, 'mutationFn'> & RequestOptions
) {
  const client = useApiClient()

  return useMutation<TData, ApiError, void>({
    mutationFn: async () => {
      const response = await client.delete<TData>(url, options as AxiosRequestConfig)
      return response.data
    },
    ...options,
  })
}

/**
 * Hook to invalidate queries
 */
export function useInvalidateQueries() {
  const queryClient = useQueryClient()

  return {
    invalidate: (keys: QueryKey | QueryKey[]) => {
      if (Array.isArray(keys[0])) {
        (keys as QueryKey[]).forEach(key => queryClient.invalidateQueries({ queryKey: key }))
      } else {
        queryClient.invalidateQueries({ queryKey: keys as QueryKey })
      }
    },
    invalidateAll: () => queryClient.invalidateQueries(),
    refetch: (keys: QueryKey) => queryClient.refetchQueries({ queryKey: keys }),
  }
}

/**
 * Hook for file uploads
 */
export function useFileUpload<TData = unknown>(
  url: string,
  options?: Omit<UseMutationOptions<TData, ApiError, FormData>, 'mutationFn'> & RequestOptions
) {
  const client = useApiClient()

  return useMutation<TData, ApiError, FormData>({
    mutationFn: async (formData: FormData) => {
      const response = await client.post<TData>(url, formData, {
        ...options,
        headers: {
          ...options?.headers,
          'Content-Type': 'multipart/form-data',
        },
      } as AxiosRequestConfig)
      return response.data
    },
    ...options,
  })
}

// =============================================================================
// AI Platform Hooks
// =============================================================================

/**
 * Types for AI explain API
 */
export interface ExplainScoreRequest {
  domain: string
  entity_id: string
  entity_type: string
  entity: Record<string, unknown>
}

export interface ExplainScoreResponse {
  entity_id: string
  entity_type: string
  base_score: number
  final_score: number
  signals: Array<{
    name: string
    raw_value: unknown
    transformed: number
    weight: number
    contribution: number
    explanation: string
  }>
  boosts: Array<{
    name: string
    matched: boolean
    contribution: number
    explanation: string
  }>
  policy_version: string
  computed_at: string
}

/**
 * Hook to explain a single entity's AI score
 */
export function useExplainScore(orgId?: string) {
  const client = useApiClient()

  return useMutation<ExplainScoreResponse, ApiError, ExplainScoreRequest>({
    mutationFn: async (request) => {
      const params = orgId ? `?org_id=${orgId}` : ''
      const response = await client.post<ExplainScoreResponse>(
        `/api/ai/explain/score/${params}`,
        request
      )
      return response.data
    },
  })
}

/**
 * Types for AI event logging
 */
export interface LogExposureRequest {
  event_type: string
  entity_type: string
  entity_ids: string[]
  rankings?: Record<string, number>
  page_context?: string
  filters_applied?: Record<string, unknown>
  policy_version?: string
  session_id?: string
}

export interface LogOutcomeRequest {
  event_type: string
  entity_type: string
  entity_id: string
  action_details?: Record<string, unknown>
  exposure_event_id?: string
  exposure_rank?: number
  time_since_exposure_ms?: number
  page_context?: string
  policy_version?: string
  session_id?: string
}

export interface LogRankingClickRequest {
  entity_type: string
  clicked_entity_id: string
  exposure_rankings: Record<string, number>
  page_context?: string
  policy_version?: string
  session_id?: string
}

/**
 * Hook to log AI exposure events
 */
export function useLogExposure(orgId?: string) {
  const client = useApiClient()

  return useMutation<{ exposure_id: string; status: string }, ApiError, LogExposureRequest>({
    mutationFn: async (request) => {
      const params = orgId ? `?org_id=${orgId}` : ''
      const response = await client.post(`/api/ai/events/exposure/${params}`, request)
      return response.data
    },
  })
}

/**
 * Hook to log AI outcome events
 */
export function useLogOutcome(orgId?: string) {
  const client = useApiClient()

  return useMutation<{ outcome_id: string; status: string }, ApiError, LogOutcomeRequest>({
    mutationFn: async (request) => {
      const params = orgId ? `?org_id=${orgId}` : ''
      const response = await client.post(`/api/ai/events/outcome/${params}`, request)
      return response.data
    },
  })
}

/**
 * Hook to log ranking clicks (convenience wrapper)
 */
export function useLogRankingClick(orgId?: string) {
  const client = useApiClient()

  return useMutation<{ outcome_id: string; clicked_rank: number; status: string }, ApiError, LogRankingClickRequest>({
    mutationFn: async (request) => {
      const params = orgId ? `?org_id=${orgId}` : ''
      const response = await client.post(`/api/ai/events/ranking-click/${params}`, request)
      return response.data
    },
  })
}

